<template>
  <b-container fluid>
    <b-row class="mb-2">
      <b-col class="px-sm-3 px-0">
        <h1>Create Coupon</h1>
      </b-col>
    </b-row>

    <!-- Error State -->
    <b-alert
      v-if="error"
      variant="danger"
      show
      dismissible
      @dismissed="error = null"
    >
      {{ error }}
    </b-alert>

    <!-- Validation Errors -->
    <b-alert v-if="Object.keys(formErrors).length > 0" variant="danger" show>
      <div v-for="(messages, field) in formErrors" :key="field">
        <strong>{{ field }}:</strong>
        <span v-if="Array.isArray(messages)">{{ messages.join(', ') }}</span>
        <span v-else>{{ messages }}</span>
      </div>
    </b-alert>

    <b-form @submit.prevent="onSubmit">
      <b-row>
        <!-- Left Column: Basic Details -->
        <b-col md="6">
          <b-form-group label="Name" description="Internal use only.">
            <b-form-input
              v-model="form.name"
              placeholder="Enter name..."
              required
              :state="formErrors.name ? false : null"
            />
          </b-form-group>

          <b-form-group
            label="Code"
            description="What the user types to redeem coupon (case insensitive)."
          >
            <b-form-input
              v-model="form.code"
              placeholder="Enter code..."
              required
              :state="formErrors.code ? false : null"
            />
          </b-form-group>

          <b-form-group label="Description">
            <b-form-input
              v-model="form.description"
              placeholder="Internal coupon notes..."
            />
          </b-form-group>
        </b-col>

        <!-- Right Column: Configuration -->
        <b-col md="6">
          <b-form-group label="Type">
            <b-form-select v-model="form.type" :options="typeOptions" />
          </b-form-group>

          <b-form-group v-show="form.type === 'amount'" label="Discount Amount">
            <currency-input v-model="amountOff" class="form-control" />
          </b-form-group>

          <b-form-group v-show="form.type === 'percent'" label="Percentage Off">
            <b-form-input
              v-model="percentageOff"
              type="number"
              min="0"
              step="1"
              max="100"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mt-3 mb-5">
        <b-col>
          <b-button type="submit" variant="primary" :disabled="saving">
            <b-spinner v-if="saving" small class="mr-2"></b-spinner>
            Create Coupon
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import CouponsAPI from '../../services/CouponsAPI';

export default {
  name: 'CouponCreate',
  data() {
    return {
      saving: false,
      error: null,
      formErrors: {},
      amountOff: 0,
      percentageOff: 0,
      form: {
        name: '',
        code: '',
        description: '',
        active: true,
        auto_apply: false,
        type: 'percent',
        target: 'brand',
        paid_by: 'brand',
        promotional: 0,
      },
      typeOptions: [
        { value: 'amount', text: 'Amount Off ($)' },
        { value: 'percent', text: 'Percentage Off (%)' },
      ],
    };
  },
  computed: {
    ...mapGetters('session', ['canRefundOrders', 'isAdmin']),
    canAccessCoupons() {
      return this.canRefundOrders || this.isAdmin;
    },
  },
  created() {
    if (!this.canAccessCoupons) {
      this.$router.push('/');
    }
  },
  methods: {
    async onSubmit() {
      if (!this.form.name || !this.form.code) {
        this.formErrors = {};
        if (!this.form.name) this.formErrors.name = ['Name is required'];
        if (!this.form.code) this.formErrors.code = ['Code is required'];
        return;
      }

      this.saving = true;
      this.formErrors = {};
      this.error = null;

      try {
        // Build the payload
        const payload = { ...this.form };

        // Set the amount based on type
        if (payload.type === 'percent') {
          payload.amount = this.percentageOff;
        } else {
          payload.amount = this.amountOff;
        }

        await CouponsAPI.createUserCoupon(payload);

        this.$router.push('/coupons');
      } catch (error) {
        if (error.response?.data?.errors) {
          this.formErrors = error.response.data.errors;
        } else if (error.response?.data?.error) {
          this.formErrors = error.response.data.error;
        } else {
          this.error =
            error.response?.data?.message || 'Failed to create coupon';
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
